import { useEffect, useRef, useState } from 'react';
import { type Blocker, useBlocker } from 'react-router-dom';

interface UseAutoSaveOptions {
  /** Whether auto-save is enabled */
  enabled: boolean;
  /** The save function to call */
  onSave: () => void;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Whether there are unsaved user changes */
  unsavedChanges: boolean;
  /** A value that changes on every content edit. Re-arms the debounce so the
   *  timer fires after the user *pauses*, even while `unsavedChanges` stays
   *  true across consecutive edits (it only flips false once a save lands). */
  changeKey?: string | number;
  /** Whether to warn on navigation with unsaved changes */
  warnUnsavedChanges?: boolean;
  /** Skip auto-save (e.g., report not loaded or finalized) */
  skip?: boolean;
  /** Pathname prefix treated as staying on this page — no blocker warning */
  stayWithinPathPrefix?: string;
}

interface UseAutoSaveReturn {
  /** True while an auto-save is in flight */
  isAutoSaving: boolean;
  /** React Router blocker — use to render a confirmation dialog */
  blocker: Blocker;
}

/**
 * Debounced auto-save hook. When unsavedChanges becomes true,
 * starts a debounce timer and calls onSave when it fires.
 */
export function useAutoSave({
  enabled,
  onSave,
  debounceMs = 1500,
  unsavedChanges,
  changeKey,
  warnUnsavedChanges = true,
  skip = false,
  stayWithinPathPrefix,
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  // AP-778 §7.1 (autosave on): a pending debounced save must not be lost to
  // navigation — the debounce cleanup below clears the timer on unmount, so
  // without this a study switch mid-debounce silently dropped the edit.
  // Flushed fire-and-forget; the mutation outlives the unmounted component.
  //
  // `lastRequestedSaveKeyRef` records the changeKey the debounce last fired
  // for: unsavedChanges stays true until that save LANDS, so without the key
  // comparison a navigate-right-after-autosave-fires would send the same
  // save twice. The flush only covers edits no save was requested for.
  const lastRequestedSaveKeyRef = useRef<string | number | undefined | null>(
    null
  );
  const flushArmedRef = useRef(false);
  flushArmedRef.current =
    unsavedChanges &&
    enabled &&
    !skip &&
    changeKey !== lastRequestedSaveKeyRef.current;
  useEffect(
    () => () => {
      if (flushArmedRef.current) onSaveRef.current();
    },
    []
  );

  useEffect(() => {
    if (!unsavedChanges || !enabled || skip) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setIsAutoSaving(true);
      lastRequestedSaveKeyRef.current = changeKey;
      onSaveRef.current();
      setTimeout(() => setIsAutoSaving(false), 500);
    }, debounceMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // `changeKey` re-arms the debounce on each edit so the save fires after the
    // user pauses, not just on the first false→true transition of
    // `unsavedChanges`.
  }, [unsavedChanges, changeKey, enabled, skip, debounceMs]);

  // Warn on navigation when there are unsaved changes and warning is enabled.
  // Autosave covers persistence — when it's on, the warning is redundant noise.
  const shouldBlock = unsavedChanges && warnUnsavedChanges && !enabled && !skip;

  // Browser navigation (refresh, close tab)
  useEffect(() => {
    if (!shouldBlock) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldBlock]);

  // Tab close with autosave on: best-effort save attempt, no prompt (the
  // save is fire-and-forget by decision). The request may not survive unload
  // in every browser — documented limitation (AP-778 §7.1).
  const attemptSaveOnUnload = unsavedChanges && enabled && !skip;
  useEffect(() => {
    if (!attemptSaveOnUnload) return undefined;
    // Same duplicate guard as the unmount flush: skip when the debounce
    // already requested a save for the current edit.
    const handler = () => {
      if (flushArmedRef.current) onSaveRef.current();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [attemptSaveOnUnload]);

  // React Router navigation (link clicks, back/forward)
  const blocker = useBlocker(
    ({ nextLocation }) =>
      shouldBlock &&
      !(
        stayWithinPathPrefix &&
        nextLocation.pathname.startsWith(stayWithinPathPrefix)
      )
  );

  return { isAutoSaving, blocker };
}
